// src/components/common/AppModal.tsx
import React from "react";
import ReactDOM from "react-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faXmark } from "@fortawesome/free-solid-svg-icons";
import "./AppModal.scss";

interface AppModalProps {
  isOpen: boolean;
  onClose: () => void;
  width?: number | string;
  maxHeight?: number | string;
  /**
   * Floor height for the modal. Use to keep the dialog a stable size when
   * inner content (e.g., tabs) varies — prevents the centered modal from
   * jumping around the screen between tab switches.
   */
  minHeight?: number | string;
  children: React.ReactNode;
  showClose?: boolean;
  /** When true, clicking the overlay backdrop will NOT close the modal */
  persistent?: boolean;
  /** Optional header content rendered in a styled bar at the top of the modal. */
  header?: React.ReactNode;
  /** Apply the accent background color to the header (e.g., wildfire coral, VAR red). */
  headerAccent?: boolean;
}

const AppModal: React.FC<AppModalProps> = ({
  isOpen,
  onClose,
  width = 420,
  maxHeight,
  minHeight,
  children,
  showClose = true,
  persistent = false,
  header,
  headerAccent = false,
}) => {
  if (!isOpen) return null;

  const handleOverlayClick = () => {
    if (!persistent) {
      onClose();
    }
  };

  const modalClass = ['app-modal', header ? 'app-modal--has-header' : '']
    .filter(Boolean)
    .join(' ');
  const headerClass = ['app-modal__header', headerAccent ? 'app-modal__header--accent' : '']
    .filter(Boolean)
    .join(' ');

  return ReactDOM.createPortal(
    <div className="app-modal__overlay" onClick={handleOverlayClick}>
      <div
        className={modalClass}
        style={{
          width,
          ...(maxHeight && { maxHeight }),
          ...(minHeight && { minHeight }),
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {showClose && (
          <button
            className="app-modal__close-icon"
            onClick={onClose}
            aria-label="Close"
          >
            <FontAwesomeIcon icon={faXmark} />
          </button>
        )}

        {header && <div className={headerClass}>{header}</div>}

        {/* Scrollable area so the close button stays fixed */}
        <div className="app-modal__body">{children}</div>
      </div>
    </div>,
    document.body
  );
};

export default AppModal;
